import { useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  usePage,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { plainText } from './propertyUtils';
import { SiteMenu, useScrollToTopOnPageLoad } from './SiteMenu';
import { NavLogo } from './NavLogo';

const LOGO_DEFAULT = '/american-landmark-logo-white.png';

export const socialDef: SlotGroupDefinition = {
  label: 'Social Links',
  fields: {
    instagram: {
      label: 'Instagram URL',
      type: 'url',
      defaultValue: 'https://www.instagram.com/americanlandmark/',
    },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    facebook: {
      label: 'Facebook URL',
      type: 'url',
      defaultValue: 'https://www.facebook.com/AmericanLandmarkApartments/',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

export const investorDef: SlotGroupDefinition = {
  label: 'Investor Login',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.alapts.com/investors',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Investor Login',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

export const portalDef: SlotGroupDefinition = {
  label: 'Resident Portal',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/resident-portal',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Resident Portal',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export const contactLinkDef: SlotGroupDefinition = {
  label: 'Contact Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const NAV_LINKS = [
  { href: '/our-communities', label: 'OUR COMMUNITIES' },
  { href: '/investors', label: 'INVEST WITH US', sora: true },
  { href: '/careers', label: 'WORK WITH US' },
];

function SocialIcon({ name }: { name: 'instagram' | 'linkedin' | 'facebook' }) {
  if (name === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
        <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm5 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm6.5-.9a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z" />
      </svg>
    );
  }
  if (name === 'linkedin') {
    return (
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
        <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8.5h4V23h-4V8.5zM8.5 8.5h3.8v2h.05c.53-1 1.84-2.05 3.79-2.05 4.05 0 4.8 2.67 4.8 6.14V23h-4v-6.6c0-1.57-.03-3.59-2.19-3.59-2.19 0-2.53 1.71-2.53 3.48V23h-3.92V8.5z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
      <path d="M22.7 0H1.3C.6 0 0 .6 0 1.3v21.4C0 23.4.6 24 1.3 24h11.5v-9.3H9.7V11h3.1V8.4c0-3.1 1.9-4.8 4.7-4.8 1.3 0 2.5.1 2.8.1v3.3h-1.9c-1.5 0-1.8.7-1.8 1.8V11h3.6l-.5 3.7h-3.1V24h6.1c.7 0 1.3-.6 1.3-1.3V1.3C24 .6 23.4 0 22.7 0z" />
    </svg>
  );
}

function SocialCircle({
  href,
  target,
  label,
  name,
}: {
  href: string;
  target: string;
  label: string;
  name: 'instagram' | 'linkedin' | 'facebook';
}) {
  return (
    <a
      href={href}
      target={target}
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/25 text-[rgb(var(--ala-on-primary))] transition hover:border-[rgb(var(--ala-accent))] hover:bg-[rgb(var(--ala-accent))] hover:text-[rgb(var(--ala-on-primary))]"
    >
      <SocialIcon name={name} />
    </a>
  );
}

export function Header() {
  const page = usePage();
  const isHome = page.alias === 'home';
  const isLandmark360 = page.alias === 'landmark360';
  const isOurCommunities = page.alias === 'our-communities' || page.alias === 'our-communities-v2';
  const isAbout = page.alias === 'about';
  const isTeam = page.alias === 'team';
  const isCareers = page.alias === 'careers';
  const isInvestors = page.alias === 'investors' || page.alias === 'investors-copy';
  const isImpact = page.alias === 'impact';
  const isStrategy = page.alias === 'strategy';
  const isCulture = page.alias === 'culture';
  const isContact = page.alias === 'contact';
  const isFaqs = page.alias === 'faqs';
  const isPrivacy = page.alias === 'privacy';
  const isAccessibility = page.alias === 'accessibility';
  const isAlOne = page.alias === 'al-one';
  const isPhotoHeroOverlay =
    isAlOne ||
    isLandmark360 ||
    isOurCommunities ||
    isAbout ||
    isTeam ||
    isCareers ||
    isInvestors ||
    isImpact ||
    isStrategy ||
    isCulture ||
    isContact ||
    isFaqs ||
    isPrivacy ||
    isAccessibility;
  const isOverlayHeader = isHome || isPhotoHeroOverlay;
  const logo = useContentToken('header.company_logo');
  const logoAlt = plainText(useContentToken('header.company_logo_alt'));
  const menuBackground = useContentToken('header.menu_background');
  const social = useSlotGroup('header.social', socialDef);
  const investor = useSlotGroup('header.investor_login', investorDef);
  const portal = useSlotGroup('header.resident_portal', portalDef);
  const contact = useSlotGroup('header.contact_link', contactLinkDef);
  const socialTarget = social._target || '_blank';
  // Photo heroes overlay dark imagery — always use the white mark.
  const logoUrl = isPhotoHeroOverlay ? LOGO_DEFAULT : logo || LOGO_DEFAULT;
  const [scrolled, setScrolled] = useState(false);
  useScrollToTopOnPageLoad();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const headerPosition = isOverlayHeader ? 'fixed inset-x-0 top-0' : 'sticky top-0';

  const menuTrigger = (
    <button
      type="button"
      className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition lg:h-[60px] lg:w-[60px] ${
        scrolled
          ? 'bg-[rgb(var(--ala-primary))]/10 text-[rgb(var(--ala-primary))] hover:bg-[rgb(var(--ala-primary))]/20'
          : 'bg-[rgb(var(--ala-on-primary))]/10 text-[rgb(var(--ala-on-primary))] hover:bg-[rgb(var(--ala-on-primary))]/20'
      }`}
      aria-label="Open menu"
    >
      <span className="flex flex-col gap-1.5" aria-hidden>
        <span className="block h-0.5 w-5 bg-current lg:w-6" />
        <span className="block h-0.5 w-5 bg-current lg:w-6" />
        <span className="block h-0.5 w-5 bg-current lg:w-6" />
      </span>
    </button>
  );

  return (
    <header className={`${headerPosition} z-40 text-[rgb(var(--ala-on-primary))]`}>
      {/* Top utility bar */}
      <div className="bg-[rgb(var(--ala-primary))]">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-4 py-2 md:px-8 lg:px-12">
          <EditableSlotGroup groupPath="header.social" definition={socialDef}>
            <div className="flex items-center gap-2.5">
              {social.instagram ? (
                <SocialCircle href={social.instagram} target={socialTarget} label="Instagram" name="instagram" />
              ) : null}
              {social.linkedin ? (
                <SocialCircle href={social.linkedin} target={socialTarget} label="LinkedIn" name="linkedin" />
              ) : null}
              {social.facebook ? (
                <SocialCircle href={social.facebook} target={socialTarget} label="Facebook" name="facebook" />
              ) : null}
            </div>
          </EditableSlotGroup>

          <div className="hidden items-center gap-x-6 font-heading text-[11px] uppercase tracking-[0.14em] text-[rgb(var(--ala-on-primary))]/80 md:flex">
            <EditableSlotGroup groupPath="header.investor_login" definition={investorDef}>
              <a
                href={investor.url || 'https://www.alapts.com/investors'}
                target={investor._target || '_blank'}
                rel="noopener noreferrer"
                className="transition hover:text-[rgb(var(--ala-accent))]"
              >
                {investor.label || 'Investor Login'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="header.resident_portal" definition={portalDef}>
              <a
                href={portal.url || '/resident-portal'}
                target={portal._target || '_self'}
                className="transition hover:text-[rgb(var(--ala-accent))]"
              >
                {portal.label || 'Resident Portal'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="header.contact_link" definition={contactLinkDef}>
              <a
                href={contact.url || '/contact-us'}
                target={contact._target || '_self'}
                className="transition hover:text-[rgb(var(--ala-accent))]"
              >
                {contact.label || 'Contact Us'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      {/* Inner-page nav (home hero owns its own header) */}
      {!isHome ? (
        <div
          className={`border-b transition-[background-color,border-color,box-shadow] duration-500 ease-out ${
            scrolled
              ? 'border-transparent bg-[#ECECEC] shadow-lg'
              : `border-[rgb(var(--ala-on-primary))]/10 ${
                  isPhotoHeroOverlay ? 'bg-[#ECECEC]/0' : 'bg-[rgb(var(--ala-primary))]'
                }`
          }`}
        >
          <div
            className={`mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-4 transition-[padding] duration-500 ease-out md:px-8 lg:px-12 ${
              scrolled ? 'py-2' : 'py-3'
            }`}
          >
            <a href="/" className="shrink-0">
              <EditableToken path="header.company_logo" type="image" as="div" label="Company Logo">
                <NavLogo url={logoUrl} alt={logoAlt || 'American Landmark'} docked={scrolled} />
              </EditableToken>
            </a>
            <div className="sr-only">
              <RichTextContent path="header.company_logo_alt" label="Company Logo Alt Text" as="span" />
            </div>

            <nav className="hidden min-w-0 flex-1 items-center justify-end gap-4 lg:flex lg:gap-8">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`whitespace-nowrap text-[18px] uppercase tracking-[0.3px] transition-colors hover:text-[rgb(var(--ala-accent))] ${
                    scrolled ? 'text-[rgb(var(--ala-primary))]' : 'text-[rgb(var(--ala-on-primary))]'
                  } ${link.sora ? 'font-sora font-medium' : 'font-heading font-medium'}`}
                >
                  {link.label}
                </a>
              ))}
              <SiteMenu
                logoUrl={logoUrl}
                logoAlt={logoAlt || 'American Landmark'}
                backgroundUrl={menuBackground}
                trigger={menuTrigger}
              />
            </nav>

            <div className="lg:hidden">
              <SiteMenu
                logoUrl={logoUrl}
                logoAlt={logoAlt || 'American Landmark'}
                backgroundUrl={menuBackground}
                trigger={menuTrigger}
              />
            </div>
          </div>
          <div className="sr-only">
            <EditableToken path="header.menu_background" type="image" as="div" label="Menu Background">
              <span>Menu Background</span>
            </EditableToken>
          </div>
        </div>
      ) : (
        <div className="sr-only">
          <EditableToken path="header.company_logo" type="image" as="div" label="Company Logo">
            <span>{logoAlt || 'American Landmark'}</span>
          </EditableToken>
          <RichTextContent path="header.company_logo_alt" label="Company Logo Alt Text" as="span" />
        </div>
      )}
    </header>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.company_logo': {
      label: 'Company Logo',
      type: 'image',
      suggestedFields: ['property_company_logo', 'property_company_mono_black_logo'],
      defaultValue: '/american-landmark-logo-white.png',
    },
    'header.company_logo_alt': {
      label: 'Company Logo Alt Text',
      type: 'richtext',
      defaultField: 'property_company_name',
      defaultValue: '<p>American Landmark</p>',
    },
    'header.menu_background': {
      label: 'Menu Background',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/company/5695708787113984/01a0f3b3-4638-77ec-931c-faa1b0b98304.webp',
    },
  },
  slotGroups: {
    'header.social': socialDef,
    'header.investor_login': investorDef,
    'header.resident_portal': portalDef,
    'header.contact_link': contactLinkDef,
  },
};

(Header as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Header;
